import {lazy} from "react";
import type {ComponentType} from "react";

import {tryAcquireReloadLock} from "@/src/modules/core/lib/app-reload-coordinator";

type PageLoader<T extends ComponentType<unknown>> = () => Promise<{default: T}>;

/**
 * `lazy()` with stale-chunk recovery: after a deploy, old shells reference
 * chunk hashes that no longer exist. On load failure, force one reload
 * (guarded by app-reload-coordinator against loops), then rethrow.
 */
function lazyPage<T extends ComponentType<unknown>>(loader: PageLoader<T>) {
  return lazy(() =>
    loader().catch((error: unknown) => {
      if (typeof window !== "undefined" && navigator.onLine && tryAcquireReloadLock()) {
        window.location.reload();
      }
      throw error;
    }),
  );
}

// ---- Phase 1: pilot ----
export const LazyHelpDebugPage = lazyPage(
  () => import("@/src/modules/help/pages/help-debug-page"),
);

// ---- Phase 2: support / admin / low-traffic ----
export const LazySupportPage = lazyPage(() =>
  import("@/src/modules/support/pages").then((m) => ({default: m.SupportPage})),
);
export const LazyViewAsUserPage = lazyPage(() =>
  import("@/src/modules/support/pages").then((m) => ({default: m.ViewAsUserPage})),
);
export const LazyRbacAdminPage = lazyPage(
  () => import("@/src/modules/rbac-admin/pages/rbac-admin-page"),
);
export const LazyReportsPage = lazyPage(() =>
  import("@/src/modules/reports/pages").then((m) => ({default: m.ReportsPage})),
);
export const LazyBriefingBoardsPage = lazyPage(() =>
  import("@/src/modules/briefing-boards/pages").then((m) => ({default: m.BriefingBoardsPage})),
);
export const LazyDispatchCentersPage = lazyPage(() =>
  import("@/src/modules/dispatch-centers/pages").then((m) => ({default: m.DispatchCentersPage})),
);
export const LazyPatrolRoutesPage = lazyPage(
  () => import("@/src/modules/patrol-routes/pages/patrol-routes-page"),
);
export const LazyCompanyPage = lazyPage(() =>
  import("@/src/modules/settings/pages").then((m) => ({default: m.CompanyPage})),
);
export const LazyCompanyVehiclesPage = lazyPage(() =>
  import("@/src/modules/company-vehicles/pages").then((m) => ({default: m.CompanyVehiclesPage})),
);
export const LazyCompanyVehicleDetailPage = lazyPage(() =>
  import("@/src/modules/company-vehicles/pages").then((m) => ({
    default: m.CompanyVehicleDetailPage,
  })),
);
export const LazyOfficerOnGuardPage = lazyPage(() =>
  import("@/src/modules/company-on-guard/pages").then((m) => ({default: m.OfficerOnGuardPage})),
);
export const LazyPanicButtonSettingsPage = lazyPage(() =>
  import("@/src/modules/panic-button-settings/pages").then((m) => ({
    default: m.PanicButtonSettingsPage,
  })),
);

// ---- Phase 3: management / parking / vehicles / properties ----
export const LazyCitationsPage = lazyPage(() =>
  import("@/src/modules/citations/pages").then((m) => ({default: m.CitationsPage})),
);
export const LazySafelistingsPage = lazyPage(() =>
  import("@/src/modules/safelistings/pages").then((m) => ({default: m.SafelistingsPage})),
);
export const LazyStoredVehiclesPage = lazyPage(() =>
  import("@/src/modules/stored-vehicles/pages").then((m) => ({default: m.StoredVehiclesPage})),
);
export const LazyVehiclesPage = lazyPage(() =>
  import("@/src/modules/vehicles/pages/main").then((m) => ({default: m.VehiclesPage})),
);
export const LazyVehicleDetails = lazyPage(() =>
  import("@/src/modules/vehicles/pages/details").then((m) => ({default: m.VehicleDetails})),
);
export const LazyPermanentParkingPage = lazyPage(() =>
  import("@/src/modules/permanent-parking/pages").then((m) => ({default: m.PermanentParkingPage})),
);
export const LazyPermanentParkingCreatePage = lazyPage(() =>
  import("@/src/modules/permanent-parking/pages").then((m) => ({
    default: m.PermanentParkingCreatePage,
  })),
);
export const LazyPermanentParkingTaskDetailPage = lazyPage(() =>
  import("@/src/modules/permanent-parking/pages").then((m) => ({
    default: m.PermanentParkingTaskDetailPage,
  })),
);
export const LazyTasksPage = lazyPage(() =>
  import("@/src/modules/tasks/pages").then((m) => ({default: m.TasksPage})),
);
export const LazyTaskDetailPage = lazyPage(
  () => import("@/src/modules/tasks/pages/task-detail-page"),
);
export const LazyUsersPage = lazyPage(() => import("@/src/modules/users/pages"));
export const LazyUserDetailPage = lazyPage(
  () => import("@/src/modules/users/pages/user-detail-page"),
);
export const LazyPropertiesPage = lazyPage(() =>
  import("@/src/modules/properties/pages").then((m) => ({default: m.PropertiesPage})),
);
export const LazyPropertyDetails = lazyPage(
  () => import("@/src/modules/properties/pages/details"),
);
export const LazyPropertyPoolsPage = lazyPage(
  () => import("@/src/modules/property-pools/pages/property-pools-page"),
);
export const LazyTennisAmenitiesPage = lazyPage(
  () => import("@/src/modules/tennis-amenities/pages/tennis-amenities-page"),
);
export const LazyGymAmenitiesPage = lazyPage(
  () => import("@/src/modules/gym-amenities/pages/gym-amenities-page"),
);
export const LazyRecreationAmenitiesPage = lazyPage(
  () => import("@/src/modules/recreation-amenities/pages/recreation-amenities-page"),
);
export const LazyHouseholdsPage = lazyPage(() =>
  import("@/src/modules/households/pages").then((m) => ({default: m.HouseholdsPage})),
);
export const LazyHouseholdDetailPage = lazyPage(() =>
  import("@/src/modules/households/pages").then((m) => ({default: m.HouseholdDetailPage})),
);
export const LazyEmergencyContactsPage = lazyPage(() =>
  import("@/src/modules/emergency-contacts/pages").then((m) => ({
    default: m.EmergencyContactsPage,
  })),
);

// ---- Phase 4: core operational ----
export const LazyDarsPage = lazyPage(() =>
  import("@/src/modules/dars/pages").then((m) => ({default: m.DarsPage})),
);
export const LazyIncidentsPage = lazyPage(
  () => import("@/src/modules/incidents/pages/incidents-page"),
);
export const LazyDispatchLogsPage = lazyPage(
  () => import("@/src/modules/dispatch-logs/pages/dispatch-logs-page"),
);
export const LazyPropertyInspectionReportsPage = lazyPage(() =>
  import("@/src/modules/property-inspection-reports/pages").then((m) => ({
    default: m.PropertyInspectionReportsPage,
  })),
);
export const LazyPoolAccessLogsPage = lazyPage(
  () => import("@/src/modules/pool-access-logs/pages/pool-access-logs-page"),
);
export const LazyTennisAccessLogsPage = lazyPage(
  () => import("@/src/modules/tennis-access-logs/pages/tennis-access-logs-page"),
);
export const LazyGymAccessLogsPage = lazyPage(
  () => import("@/src/modules/gym-access-logs/pages/gym-access-logs-page"),
);
export const LazyRecreationAccessLogsPage = lazyPage(
  () => import("@/src/modules/recreation-access-logs/pages/recreation-access-logs-page"),
);
export const LazyShiftsPage = lazyPage(() =>
  import("@/src/modules/shifts/pages").then((m) => ({default: m.ShiftsPage})),
);
export const LazyShiftDetailsPage = lazyPage(() =>
  import("@/src/modules/shifts/pages").then((m) => ({default: m.ShiftDetailsPage})),
);
export const LazyShiftHistoryPage = lazyPage(() =>
  import("@/src/modules/shifts/pages").then((m) => ({default: m.ShiftHistoryPage})),
);
export const LazyDirectMessagesPage = lazyPage(() =>
  import("@/src/modules/direct-messages/pages").then((m) => ({default: m.DirectMessagesPage})),
);
export const LazyNotificationsPage = lazyPage(() =>
  import("@/src/modules/notification-feed/pages").then((m) => ({default: m.NotificationsPage})),
);
export const LazyPanicAlertsPage = lazyPage(
  () => import("@/src/modules/panic-alerts/pages/panic-alerts-page"),
);

// ---- Phase 5: remaining ----
export const LazyResidentClaimPage = lazyPage(() =>
  import("@/src/modules/resident-claim/pages").then((m) => ({default: m.ResidentClaimPage})),
);
export const LazyResidentClaimVerifyPage = lazyPage(() =>
  import("@/src/modules/resident-claim/pages").then((m) => ({
    default: m.ResidentClaimVerifyPage,
  })),
);
